import React, { useState } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  Alert,
  ScrollView,
} from 'react-native';

interface UserTypeSelectionScreenProps {
  onBack: () => void;
  onUserTypeSelected: (userType: 'customer' | 'business-owner') => void;
  /** When provided, shows "Care coordination" option; user creates a customer account with a care role. */
  onCareSelected?: () => void;
}

const UserTypeSelectionScreen: React.FC<UserTypeSelectionScreenProps> = ({
  onBack,
  onUserTypeSelected,
  onCareSelected,
}) => {
  const [selectedType, setSelectedType] = useState<'customer' | 'business-owner' | 'care' | null>(null);

  const handleContinue = () => {
    if (!selectedType) {
      Alert.alert('Please Select', 'Please select an account type.');
      return;
    }
    if (selectedType === 'care') {
      onCareSelected?.();
    } else {
      onUserTypeSelected(selectedType);
    }
  };

  return (
    <ScrollView style={styles.container}>
      <View style={styles.header}>
        <TouchableOpacity onPress={onBack} style={styles.backButton}>
          <Text style={styles.backButtonText}>← Back</Text>
        </TouchableOpacity>
        <Text style={styles.headerTitle}>Create Account</Text>
        <View style={{ width: 50 }} />
      </View>

      {/* Content */}
      <View style={styles.content}>
        <Text style={styles.title}>Choose Your Account Type</Text>
        <Text style={styles.subtitle}>
          Select the type of account that best describes how you'll use BookAPT
        </Text>

        {/* Account Type Options */}
        <View style={styles.optionsContainer}>
          {/* User Option */}
          <TouchableOpacity
            style={[
              styles.optionCard,
              selectedType === 'customer' && styles.selectedOptionCard,
            ]}
            onPress={() => setSelectedType('customer')}
          >
            <View style={styles.optionIcon}>
              <Text style={styles.optionEmoji}>👤</Text>
            </View>
            <View style={styles.optionContent}>
              <Text style={styles.optionTitle}>{"I'm User"}</Text>
              <Text style={styles.optionDescription}>
                I want to book appointments with businesses and services
              </Text>
              <View style={styles.featureList}>
                <Text style={styles.featureItem}>• Book appointments instantly</Text>
                <Text style={styles.featureItem}>• Manage your bookings</Text>
                <Text style={styles.featureItem}>• Get reminders and updates</Text>
                <Text style={styles.featureItem}>• Pay securely online</Text>
              </View>
            </View>
            {selectedType === 'customer' && (
              <View style={styles.selectedIndicator}>
                <Text style={styles.selectedCheckmark}>✓</Text>
              </View>
            )}
          </TouchableOpacity>

          {/* Business Owner Option */}
          <TouchableOpacity
            style={[
              styles.optionCard,
              selectedType === 'business-owner' && styles.selectedOptionCard,
            ]}
            onPress={() => setSelectedType('business-owner')}
          >
            <View style={styles.optionIcon}>
              <Text style={styles.optionEmoji}>🏢</Text>
            </View>
            <View style={styles.optionContent}>
              <Text style={styles.optionTitle}>I'm a Business Owner</Text>
              <Text style={styles.optionDescription}>
                I want to manage appointments and grow my business
              </Text>
              <View style={styles.featureList}>
                <Text style={styles.featureItem}>• Manage your calendar</Text>
                <Text style={styles.featureItem}>• Accept online bookings</Text>
                <Text style={styles.featureItem}>• Track analytics and revenue</Text>
                <Text style={styles.featureItem}>• Manage your team</Text>
              </View>
            </View>
            {selectedType === 'business-owner' && (
              <View style={styles.selectedIndicator}>
                <Text style={styles.selectedCheckmark}>✓</Text>
              </View>
            )}
          </TouchableOpacity>

          {/* Care Coordination Option */}
          {onCareSelected != null && (
            <TouchableOpacity
              style={[
                styles.optionCard,
                selectedType === 'care' && styles.selectedOptionCard,
              ]}
              onPress={() => setSelectedType('care')}
            >
              <View style={styles.optionIcon}>
                <Text style={styles.optionEmoji}>❤️</Text>
              </View>
              <View style={styles.optionContent}>
                <Text style={styles.optionTitle}>Care Coordination</Text>
                <Text style={styles.optionDescription}>
                  Coordinate care for a loved one, or sign up as senior / care professional
                </Text>
                <View style={styles.featureList}>
                  <Text style={styles.featureItem}>• Family / Coordinator</Text>
                  <Text style={styles.featureItem}>• Senior / Care recipient</Text>
                  <Text style={styles.featureItem}>• Care professional</Text>
                </View>
              </View>
              {selectedType === 'care' && (
                <View style={styles.selectedIndicator}>
                  <Text style={styles.selectedCheckmark}>✓</Text>
                </View>
              )}
            </TouchableOpacity>
          )}
        </View>

        {/* Continue Button */}
        <TouchableOpacity
          style={[
            styles.continueButton,
            !selectedType && styles.continueButtonDisabled,
          ]}
          onPress={handleContinue}
          disabled={!selectedType}
        >
          <Text style={[
            styles.continueButtonText,
            !selectedType && styles.continueButtonTextDisabled,
          ]}>
            Continue
          </Text>
        </TouchableOpacity>
      </View>
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f8f9fa',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 20,
    paddingTop: 60,
    paddingBottom: 20,
    backgroundColor: '#fff',
    borderBottomWidth: 1,
    borderBottomColor: '#e1e5e9',
  },
  backButton: {
    padding: 10,
  },
  backButtonText: {
    fontSize: 16,
    color: '#007AFF',
    fontWeight: '500',
  },
  headerTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#1c1c1e',
  },
  content: {
    flex: 1,
    padding: 20,
  },
  title: {
    fontSize: 28,
    fontWeight: 'bold',
    color: '#1c1c1e',
    textAlign: 'center',
    marginBottom: 10,
  },
  subtitle: {
    fontSize: 16,
    color: '#666',
    textAlign: 'center',
    marginBottom: 40,
    lineHeight: 22,
  },
  optionsContainer: {
    marginBottom: 40,
  },
  optionCard: {
    backgroundColor: '#fff',
    borderRadius: 16,
    padding: 20,
    marginBottom: 16,
    borderWidth: 2,
    borderColor: '#e1e5e9',
    flexDirection: 'row',
    alignItems: 'flex-start',
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  selectedOptionCard: {
    borderColor: '#007AFF',
    backgroundColor: '#f0f8ff',
  },
  optionIcon: {
    width: 60,
    height: 60,
    borderRadius: 30,
    backgroundColor: '#f0f0f0',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 16,
  },
  optionEmoji: {
    fontSize: 28,
  },
  optionContent: {
    flex: 1,
  },
  optionTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#1c1c1e',
    marginBottom: 8,
  },
  optionDescription: {
    fontSize: 16,
    color: '#666',
    marginBottom: 12,
    lineHeight: 22,
  },
  featureList: {
    marginTop: 8,
  },
  featureItem: {
    fontSize: 14,
    color: '#666',
    marginBottom: 4,
    lineHeight: 20,
  },
  selectedIndicator: {
    width: 24,
    height: 24,
    borderRadius: 12,
    backgroundColor: '#007AFF',
    justifyContent: 'center',
    alignItems: 'center',
    marginLeft: 12,
  },
  selectedCheckmark: {
    color: '#fff',
    fontSize: 16,
    fontWeight: 'bold',
  },
  continueButton: {
    backgroundColor: '#007AFF',
    paddingVertical: 16,
    paddingHorizontal: 32,
    borderRadius: 12,
    alignItems: 'center',
    shadowColor: '#007AFF',
    shadowOffset: {
      width: 0,
      height: 4,
    },
    shadowOpacity: 0.3,
    shadowRadius: 8,
    elevation: 6,
  },
  continueButtonDisabled: {
    backgroundColor: '#bdc3c7',
    shadowOpacity: 0,
    elevation: 0,
  },
  continueButtonText: {
    color: '#fff',
    fontSize: 18,
    fontWeight: 'bold',
  },
  continueButtonTextDisabled: {
    color: '#8e8e93',
  },
});

export default UserTypeSelectionScreen;



